import React, { useEffect, useState } from 'react';
import {
  RefreshCw, ThumbsUp, ThumbsDown, ChevronLeft, ChevronRight,
  Calendar, ClipboardList, TrendingUp, ExternalLink, X, MessageSquare,
} from 'lucide-react';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import { feedbackApi, chatApi, eventsApi, adminApi, teamMembersApi } from '../../services/api';
import { FeedbackItem, Conversation, Event, AppUser, TeamMember } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

// ── Helpers ────────────────────────────────────────────────────────────────────

const isBinaryFeedback = (item: FeedbackItem): boolean => {
  const isBinarySummary = item.summary === 'Thumbs Up' || item.summary === 'Thumbs Down';
  const hasNoDetails = !item.details || item.details.trim() === '';
  const isOtherCategory = item.category === 'other' || !item.category;
  return isBinarySummary && hasNoDetails && isOtherCategory;
};

const PAGE_SIZE = 10;
const SLA_THRESHOLD_DAYS = 3;

const SENTIMENT_STYLES: Record<string, string> = {
  positive: 'bg-green-50 text-green-700 border border-green-200',
  negative: 'bg-red-50 text-red-700 border border-red-200',
  neutral:  'bg-yellow-50 text-yellow-700 border border-yellow-200',
};

const STATUS_STYLES: Record<string, string> = {
  'new':         'bg-blue-50 text-blue-700 border border-blue-200',
  'in_review':   'bg-yellow-50 text-yellow-700 border border-yellow-200',
  'in_progress': 'bg-yellow-50 text-yellow-700 border border-yellow-200',  // legacy
  'resolved':    'bg-green-50 text-green-700 border border-green-200',
  'closed':      'bg-gray-100 text-gray-500 border border-gray-200',
  'wont_fix':    'bg-gray-100 text-gray-500 border border-gray-200',       // legacy
  'triaged':     'bg-orange-50 text-orange-700 border border-orange-200',  // legacy
};

const STATUS_LABELS: Record<string, string> = {
  'new':         'New',
  'in_review':   'In Review',
  'in_progress': 'In Review',   // legacy
  'resolved':    'Resolved',
  'closed':      'Closed',
  'wont_fix':    'Closed',      // legacy
  'triaged':     'Triaged',     // legacy
};

const PRIORITY_STYLES: Record<string, string> = {
  'p0': 'bg-red-50 text-red-700 border border-red-200',
  'p1': 'bg-orange-50 text-orange-700 border border-orange-200',
  'p2': 'bg-yellow-50 text-yellow-700 border border-yellow-200',
  'p3': 'bg-gray-100 text-gray-500 border border-gray-200',
};

// ── Pagination ─────────────────────────────────────────────────────────────────

function Pagination({ currentPage, totalPages, total, onPage }: {
  currentPage: number; totalPages: number; total: number; onPage: (p: number) => void;
}) {
  const start = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const end   = Math.min(currentPage * PAGE_SIZE, total);
  // Show at most 5 page buttons centered around currentPage
  const pages: number[] = [];
  const halfWindow = 2;
  let lo = Math.max(1, currentPage - halfWindow);
  let hi = Math.min(totalPages, currentPage + halfWindow);
  if (hi - lo < 4) {
    lo = Math.max(1, hi - 4);
    hi = Math.min(totalPages, lo + 4);
  }
  for (let p = lo; p <= hi; p++) pages.push(p);

  return (
    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
      <p className="text-sm text-gray-600">
        {total === 0 ? '0 items' : `${start}–${end} of ${total} item${total !== 1 ? 's' : ''}`}
      </p>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPage(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        </button>
        {pages.map((p) => (
          <button
            key={p}
            onClick={() => onPage(p)}
            className={`px-3 py-1 text-sm rounded-lg transition-colors ${
              p === currentPage ? 'bg-blue-600 text-white' : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {p}
          </button>
        ))}
        <button
          onClick={() => onPage(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronRight className="w-4 h-4 text-gray-600" />
        </button>
      </div>
    </div>
  );
}

// ── Conversation Modal ─────────────────────────────────────────────────────────

function ConversationModal({
  item,
  conversations,
  loading,
  error,
  onClose,
}: {
  item: FeedbackItem;
  conversations: Conversation[];
  loading: boolean;
  error: string | null;
  onClose: () => void;
}) {
  const sentiment = item.sentiment;
  const sentimentLabel = item.summary === 'Thumbs Up' ? 'Thumbs Up'
    : item.summary === 'Thumbs Down' ? 'Thumbs Down'
    : sentiment === 'positive' ? 'Positive' : sentiment === 'negative' ? 'Negative' : 'Neutral';
  const sentimentStyle = SENTIMENT_STYLES[sentiment ?? 'neutral'] ?? SENTIMENT_STYLES.neutral;
  const userLabel = item.userName || item.userEmail || (item.userId ? `…${item.userId.slice(-8)}` : 'Anonymous');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-semibold flex-shrink-0">
              {userLabel.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{userLabel}</p>
            </div>
            <div className="ml-2">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sentimentStyle}`}>
                {sentiment === 'positive' || item.summary === 'Thumbs Up'
                  ? <ThumbsUp className="w-3 h-3" />
                  : <ThumbsDown className="w-3 h-3" />}
                {sentimentLabel}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400 font-mono">
              {dayjs(item.createdAt).format('YYYY-MM-DD HH:mm')}
            </span>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              <span className="ml-3 text-sm text-gray-500">Loading conversation…</span>
            </div>
          ) : error ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 text-sm text-yellow-800">
              {error}
            </div>
          ) : conversations.length > 0 ? (
            <div className="space-y-4">
              {conversations.map((conv, idx) => (
                <div key={conv.messageId || idx} className="space-y-2">
                  {/* User message */}
                  <div className="flex items-start gap-3 justify-end">
                    <div className="max-w-[80%]">
                      <p className="text-xs text-gray-400 text-right mb-1 font-medium">User</p>
                      <div className="bg-indigo-600 text-white rounded-2xl rounded-tr-sm px-4 py-3">
                        <p className="text-sm leading-relaxed">{conv.userMessage}</p>
                      </div>
                      <p className="text-[10px] text-gray-400 text-right mt-1">
                        {dayjs(conv.timestamp).format('HH:mm')}
                      </p>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-5">
                      {userLabel.charAt(0).toUpperCase()}
                    </div>
                  </div>
                  {/* Bot response */}
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 mt-5">
                      <MessageSquare className="w-4 h-4 text-white" />
                    </div>
                    <div className="max-w-[80%]">
                      <p className="text-xs text-gray-400 mb-1 font-medium">Assistant</p>
                      <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3">
                        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">{conv.botResponse}</p>
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1">
                        {dayjs(conv.timestamp).format('HH:mm')}
                      </p>
                      {item.messageId === conv.messageId && (
                        <span className="inline-block mt-1.5 text-[10px] font-medium px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full">
                          Feedback for this message
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {/* Feedback signal */}
              <div className="flex items-center justify-center pt-2">
                <div className={`flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-medium ${sentimentStyle}`}>
                  {sentiment === 'positive' || item.summary === 'Thumbs Up'
                    ? <ThumbsUp className="w-3.5 h-3.5" />
                    : <ThumbsDown className="w-3.5 h-3.5" />}
                  User gave this response a <strong>{sentimentLabel.toLowerCase()}</strong>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center py-12">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                <MessageSquare className="w-6 h-6 text-gray-300" />
              </div>
              <p className="text-gray-500 text-sm">No conversation history available</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function FeedbackDetailsModal({
  item,
  onClose,
}: {
  item: FeedbackItem;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">Feedback Details</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="overflow-y-auto px-6 py-5 space-y-4 text-sm">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Summary</p>
            <p className="text-gray-800 whitespace-pre-wrap">{item.summary || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Details</p>
            <p className="text-gray-800 whitespace-pre-wrap">{item.details || '—'}</p>
          </div>
          {item.detailsEnglish && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Details (English)</p>
              <p className="text-gray-800 whitespace-pre-wrap text-sm italic">{item.detailsEnglish}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Category</p>
              <p className="text-gray-800 capitalize">{item.category || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Created</p>
              <p className="text-gray-800">{dayjs(item.createdAt).format('YYYY-MM-DD HH:mm')}</p>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Binary Tab ─────────────────────────────────────────────────────────────────

function BinaryTab({
  items,
  onViewMessage,
}: {
  items: FeedbackItem[];
  onViewMessage: (item: FeedbackItem) => void;
}) {
  const [filterSentiment, setFilterSentiment] = useState<string>('');
  const [startDate,       setStartDate]       = useState('');
  const [endDate,         setEndDate]         = useState('');
  const [currentPage,     setCurrentPage]     = useState(1);

  const filtered = items.filter((item) => {
    if (filterSentiment && item.sentiment !== filterSentiment) return false;
    const created = item.createdAt.split('T')[0];
    if (startDate && created < startDate) return false;
    if (endDate   && created > endDate)   return false;
    return true;
  });

  const totalPages    = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated     = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const positiveCount = filtered.filter((i) => i.sentiment === 'positive' || i.summary === 'Thumbs Up').length;
  const negativeCount = filtered.filter((i) => i.sentiment === 'negative' || i.summary === 'Thumbs Down').length;
  const positiveRate  = filtered.length > 0 ? Math.round((positiveCount / filtered.length) * 100) : 0;

  const reset = () => { setFilterSentiment(''); setStartDate(''); setEndDate(''); setCurrentPage(1); };

  return (
    <div>
      {/* Sentiment strip */}
      <div className="px-6 pt-5 pb-4 flex items-center gap-6 border-b border-gray-100 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-green-50 border border-green-200 flex items-center justify-center">
            <ThumbsUp className="w-4 h-4 text-green-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Positive</p>
            <p className="text-sm font-semibold text-gray-900">{positiveCount}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-red-50 border border-red-200 flex items-center justify-center">
            <ThumbsDown className="w-4 h-4 text-red-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Negative</p>
            <p className="text-sm font-semibold text-gray-900">{negativeCount}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 ml-4 flex-1 max-w-xs">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs text-gray-500">Positive Rate</p>
              <p className="text-xs font-semibold text-gray-900">{positiveRate}%</p>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full transition-all duration-300"
                style={{ width: `${positiveRate}%` }}
              />
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-400 ml-auto italic hidden lg:block">
          Binary signals reflect overall sentiment trends
        </p>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3 flex-wrap">
        <select
          value={filterSentiment}
          onChange={(e) => { setFilterSentiment(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white min-w-[150px]"
        >
          <option value="">All Sentiments</option>
          <option value="positive">Positive</option>
          <option value="negative">Negative</option>
          <option value="neutral">Neutral</option>
        </select>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input type="date" value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white w-36"
            />
          </div>
          <span className="text-gray-400 text-sm">→</span>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input type="date" value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white w-36"
            />
          </div>
        </div>
        <button onClick={reset}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Reset
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Created</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Signal</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">Summary</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {paginated.length > 0 ? paginated.map((item) => {
              const isPositive = item.sentiment === 'positive' || item.summary === 'Thumbs Up';
              const isNegative = item.sentiment === 'negative' || item.summary === 'Thumbs Down';
              const sentKey = isPositive ? 'positive' : isNegative ? 'negative' : 'neutral';
              const userLabel = item.userName || item.userEmail || (item.userId ? `…${item.userId.slice(-8)}` : 'Anonymous');
              return (
                <tr
                  key={item.feedbackId}
                  onClick={() => onViewMessage(item)}
                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600 font-mono">
                      {dayjs(item.createdAt).format('YYYY-MM-DD HH:mm')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${SENTIMENT_STYLES[sentKey]}`}>
                      {isPositive ? <ThumbsUp className="w-3.5 h-3.5" /> : <ThumbsDown className="w-3.5 h-3.5" />}
                      {sentKey.charAt(0).toUpperCase() + sentKey.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                        {userLabel.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm text-gray-600 max-w-[180px] truncate" title={userLabel}>{userLabel}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500 max-w-[200px] truncate block" title={item.userEmail}>{item.userEmail || '—'}</span>
                  </td>
                  <td className="px-6 py-4 max-w-[240px]">
                    <p className="text-sm text-gray-600 truncate">{item.summary || '—'}</p>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={(e) => { e.stopPropagation(); onViewMessage(item); }}
                      disabled={!item.sessionId}
                      className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed group"
                    >
                      <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                      View
                    </button>
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan={6} className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <ThumbsUp className="w-6 h-6 text-gray-300" />
                    </div>
                    <p className="text-gray-500 text-sm">No binary feedback found</p>
                    <p className="text-gray-400 text-xs mt-1">Try adjusting your filters or select a different Knowledge Assistant</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination currentPage={currentPage} totalPages={totalPages} total={filtered.length} onPage={setCurrentPage} />
    </div>
  );
}

// ── Detailed Tab ───────────────────────────────────────────────────────────────

function DetailedTab({
  items,
  teamMembers,
  onUpdateItem,
  onViewMessage,
}: {
  items: FeedbackItem[];
  teamMembers: TeamMember[];
  onUpdateItem: (feedbackId: string, patch: Partial<FeedbackItem>) => void;
  onViewMessage: (item: FeedbackItem) => void;
}) {
  const [filterStatus,   setFilterStatus]   = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [startDate,      setStartDate]      = useState('');
  const [endDate,        setEndDate]        = useState('');
  const [currentPage,    setCurrentPage]    = useState(1);
  const [updating,       setUpdating]       = useState<Record<string, boolean>>({});

  const handlePriorityChange = async (item: FeedbackItem, priority: string) => {
    const patch: any = priority ? { priority } : { priority: null };
    setUpdating(u => ({ ...u, [`${item.feedbackId}-priority`]: true }));
    try {
      await feedbackApi.update(item.feedbackId, patch);
      onUpdateItem(item.feedbackId, patch);
    } catch {
      toast.error('Failed to update priority');
    } finally {
      setUpdating(u => ({ ...u, [`${item.feedbackId}-priority`]: false }));
    }
  };

  const handleAssigneeChange = async (item: FeedbackItem, assigneeUserId: string) => {
    const patch: any = { assigneeUserId: assigneeUserId || null };
    setUpdating(u => ({ ...u, [`${item.feedbackId}-assignee`]: true }));
    try {
      await feedbackApi.update(item.feedbackId, patch);
      onUpdateItem(item.feedbackId, patch);
    } catch {
      toast.error('Failed to update assignee');
    } finally {
      setUpdating(u => ({ ...u, [`${item.feedbackId}-assignee`]: false }));
    }
  };

  const handleStatusChange = async (item: FeedbackItem, status: string) => {
    setUpdating(u => ({ ...u, [`${item.feedbackId}-status`]: true }));
    try {
      await feedbackApi.update(item.feedbackId, { status });
      onUpdateItem(item.feedbackId, { status } as Partial<FeedbackItem>);
    } catch {
      toast.error('Failed to update status');
    } finally {
      setUpdating(u => ({ ...u, [`${item.feedbackId}-status`]: false }));
    }
  };

  const filtered = items.filter((item) => {
    if (filterStatus   && item.status   !== filterStatus)   return false;
    if (filterCategory && item.category !== filterCategory) return false;
    const created = item.createdAt.split('T')[0];
    if (startDate && created < startDate) return false;
    if (endDate   && created > endDate)   return false;
    return true;
  });

  const totalPages  = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated   = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const newCount    = filtered.filter((i) => i.status === 'new').length;
  const highPriCount = filtered.filter((i) => i.priority === 'p0' || i.priority === 'p1').length;
  const ratings     = filtered.filter((i) => i.rating !== undefined).map((i) => i.rating as number);
  const avgRating   = ratings.length > 0 ? (ratings.reduce((s, r) => s + r, 0) / ratings.length).toFixed(1) : '–';

  const reset = () => { setFilterStatus(''); setFilterCategory(''); setStartDate(''); setEndDate(''); setCurrentPage(1); };

  return (
    <div>
      {/* Metrics strip */}
      <div className="px-6 pt-5 pb-4 flex items-center gap-6 border-b border-gray-100 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center">
            <ClipboardList className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Open (New)</p>
            <p className="text-sm font-semibold text-gray-900">{newCount}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-red-50 border border-red-200 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-red-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">High Priority (P0/P1)</p>
            <p className="text-sm font-semibold text-gray-900">{highPriCount}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-purple-50 border border-purple-200 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-purple-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Avg Rating</p>
            <p className="text-sm font-semibold text-gray-900">{avgRating !== '–' ? `${avgRating} / 5` : '–'}</p>
          </div>
        </div>
        <p className="text-xs text-gray-400 ml-auto italic hidden lg:block">
          Categorised reports — triage and resolve using status and priority
        </p>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3 flex-wrap">
        <select value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white min-w-[140px]"
        >
          <option value="">All Statuses</option>
          <option value="new">New</option>
          <option value="in_review">In Review</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select value={filterCategory}
          onChange={(e) => { setFilterCategory(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white min-w-[140px]"
        >
          <option value="">All Categories</option>
          {(['accuracy','hallucination','tone','latency','ui','other'] as const).map((c) => (
            <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input type="date" value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white w-36"
            />
          </div>
          <span className="text-gray-400 text-sm">→</span>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input type="date" value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white w-36"
            />
          </div>
        </div>
        <button onClick={reset}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Reset
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Created</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">Details</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assignee</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {paginated.length > 0 ? paginated.map((item) => {
              const userLabel = item.userName || item.userEmail || (item.userId ? `…${item.userId.slice(-8)}` : 'Anonymous');
              const ageDays = Math.floor((Date.now() - new Date(item.createdAt).getTime()) / (1000 * 60 * 60 * 24));
              const isStale = (item.status === 'new' || item.status === 'in_review') && ageDays > 0;
              const assignee = item.assigneeUserId ? teamMembers.find(m => m._id === item.assigneeUserId) ?? null : null;
              const assigneeInitials = assignee ? assignee.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() : null;
              return (
                <tr key={item.feedbackId} onClick={() => onViewMessage(item)} className="hover:bg-gray-50 transition-colors cursor-pointer">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600 font-mono">
                      {dayjs(item.createdAt).format('YYYY-MM-DD HH:mm')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-700 capitalize">{item.category || '—'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {item.rating !== undefined ? (
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-gray-900">{item.rating}</span>
                        <div className="flex gap-0.5">
                          {Array.from({ length: 5 }, (_, i) => (
                            <div key={i} className={`w-2 h-2 rounded-full ${i < (item.rating ?? 0) ? 'bg-blue-500' : 'bg-gray-200'}`} />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 max-w-[260px]">
                    <div className="relative group inline-block w-full">
                      <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed cursor-default">
                        {item.details || item.summary || '—'}
                      </p>
                      {item.detailsEnglish && (
                        <div className="absolute bottom-[calc(100%+6px)] left-0 z-50 hidden group-hover:block w-72 rounded-lg bg-gray-900 text-gray-100 text-xs leading-relaxed shadow-xl p-3 pointer-events-none">
                          <span className="font-semibold text-gray-300 block mb-1">English</span>
                          {item.detailsEnglish}
                          <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-900" />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                        {userLabel.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm text-gray-600 max-w-[180px] truncate" title={userLabel}>{userLabel}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <span className="text-sm text-gray-500 max-w-[200px] truncate block" title={item.userEmail}>{item.userEmail || '—'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="flex flex-col gap-1">
                      <select
                        value={item.status}
                        disabled={updating[`${item.feedbackId}-status`]}
                        onChange={e => handleStatusChange(item, e.target.value)}
                        className={`text-xs font-medium px-2 py-0.5 rounded-full border focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:opacity-50 cursor-pointer ${
                          STATUS_STYLES[item.status] ?? 'bg-gray-100 text-gray-500 border-gray-200'
                        }`}
                      >
                        <option value="new">New</option>
                        <option value="in_review">In Review</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                        {item.status === 'triaged' && <option value="triaged">Triaged</option>}
                        {item.status === 'in_progress' && <option value="in_progress">In Review</option>}
                        {item.status === 'wont_fix' && <option value="wont_fix">Closed</option>}
                      </select>
                      {isStale && (
                        <span className="text-[10px] font-medium text-red-500">{ageDays}d · overdue</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={item.priority ?? ''}
                      disabled={updating[`${item.feedbackId}-priority`]}
                      onChange={e => handlePriorityChange(item, e.target.value)}
                      className={`text-xs font-medium px-3 py-0.5 rounded-full border focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:opacity-50 cursor-pointer ${
                        item.priority
                          ? (PRIORITY_STYLES[item.priority] ?? 'bg-gray-100 text-gray-500 border-gray-200')
                          : 'bg-white text-gray-400 border-gray-300'
                      }`}
                    >
                      <option value="">— None —</option>
                      <option value="p0">P0</option>
                      <option value="p1">P1</option>
                      <option value="p2">P2</option>
                      <option value="p3">P3</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      {assigneeInitials && (
                        <div className="w-7 h-7 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center text-[10px] font-semibold flex-shrink-0" title={assignee?.name}>
                          {assigneeInitials}
                        </div>
                      )}
                      <select
                        value={item.assigneeUserId ?? ''}
                        disabled={updating[`${item.feedbackId}-assignee`]}
                        onChange={e => handleAssigneeChange(item, e.target.value)}
                        className="text-xs px-2 py-0.5 rounded-full border border-gray-200 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:opacity-50 max-w-[130px] cursor-pointer"
                      >
                        <option value="">Unassigned</option>
                        {teamMembers.map(m => (
                          <option key={m._id} value={m._id}>{m.name}</option>
                        ))}
                        {item.assigneeUserId && !teamMembers.find(m => m._id === item.assigneeUserId) && (
                          <option value={item.assigneeUserId}>…{item.assigneeUserId.slice(-6)}</option>
                        )}
                      </select>
                    </div>
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan={9} className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <ClipboardList className="w-6 h-6 text-gray-300" />
                    </div>
                    <p className="text-gray-500 text-sm">No detailed feedback found</p>
                    <p className="text-gray-400 text-xs mt-1">Try adjusting your filters or select a different Knowledge Assistant</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination currentPage={currentPage} totalPages={totalPages} total={filtered.length} onPage={setCurrentPage} />
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

const Feedback: React.FC = () => {
  const { isAdmin, user } = useAuth();

  const [activeTab,        setActiveTab]        = useState<'binary' | 'detailed'>('binary');
  const [loading,          setLoading]          = useState(false);
  const [allItems,         setAllItems]         = useState<FeedbackItem[]>([]);
  const [teamMembers,      setTeamMembers]      = useState<TeamMember[]>([]);

  const [events,           setEvents]           = useState<Event[]>([]);
  const [selectedEventId,  setSelectedEventId]  = useState<string>('');
  const [clients,          setClients]          = useState<AppUser[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [loadingClients,   setLoadingClients]   = useState(false);

  // Conversation modal state
  const [viewingItem,       setViewingItem]       = useState<FeedbackItem | null>(null);
  const [conversations,     setConversations]     = useState<Conversation[]>([]);
  const [loadingConv,       setLoadingConv]       = useState(false);
  const [convError,         setConvError]         = useState<string | null>(null);

  const binaryItems   = allItems.filter(isBinaryFeedback);
  const detailedItems = allItems.filter((i) => !isBinaryFeedback(i));

  // ── fetch helpers ────────────────────────────────────────────────────────────

  const fetchClients = async () => {
    if (!isAdmin) return;
    setLoadingClients(true);
    try {
      const resp = await adminApi.getAllUsers({ role: 'client', limit: 1000, isActive: true });
      setClients((resp.data as any)?.data?.users || []);
    } catch {
      // ignore
    } finally {
      setLoadingClients(false);
    }
  };

  const fetchEvents = async (clientId?: string) => {
    try {
      const params: any = { limit: 100 };
      if (isAdmin && clientId) params.clientId = clientId;
      else if (!isAdmin && user?._id) params.clientId = user._id;
      const resp = await eventsApi.getAll(params);
      const evList: Event[] = resp.data.data || [];
      setEvents(evList);
      if (!isAdmin && evList.length > 0) {
        setSelectedEventId(evList[0]._id);
      }
    } catch {
      // ignore
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const resp = await teamMembersApi.list();
      setTeamMembers((resp.data as any)?.data || []);
    } catch {
      // ignore — admin won't have access; that's fine
    }
  };

  const handleUpdateItem = (feedbackId: string, patch: Partial<FeedbackItem>) => {
    setAllItems(prev =>
      prev.map(item => item.feedbackId === feedbackId ? { ...item, ...patch } : item),
    );
  };

  const fetchFeedback = async (eventId: string) => {
    setLoading(true);
    try {
      const resp = await feedbackApi.list({ limit: 500, eventId } as any);
      setAllItems((resp.data as any)?.data || []);
    } catch {
      setAllItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewMessage = async (item: FeedbackItem) => {
    setViewingItem(item);
    setConversations([]);
    setConvError(null);
    if (!item.sessionId) {
      setConvError('No session ID available for this feedback.');
      return;
    }
    setLoadingConv(true);
    try {
      const resp = await chatApi.getHistory(item.sessionId);
      const convos: Conversation[] = (resp.data as any)?.data?.conversations || [];
      setConversations(convos);
      if (convos.length === 0) setConvError('No conversation history found.');
    } catch (err: any) {
      setConvError(err.response?.data?.message || err.message || 'Unable to load conversation history.');
    } finally {
      setLoadingConv(false);
    }
  };

  // ── effects ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isAdmin) fetchClients();
    fetchEvents();
    if (!isAdmin) fetchTeamMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      fetchEvents(selectedClientId || undefined);
      setSelectedEventId('');
      setAllItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  useEffect(() => {
    if (selectedEventId) fetchFeedback(selectedEventId);
    else setAllItems([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventId]);

  // ── tab config ───────────────────────────────────────────────────────────────

  const tabs = [
    {
      id: 'binary' as const,
      label: 'Binary Feedback',
      icon: <ThumbsUp className="w-4 h-4" />,
      description: 'Thumbs up / down signals — useful for tracking sentiment trends',
      count: binaryItems.length,
      accentClass: 'text-green-600 bg-green-50 border-green-200',
    },
    {
      id: 'detailed' as const,
      label: 'Detailed Feedback',
      icon: <ClipboardList className="w-4 h-4" />,
      description: 'Categorised reports with context — actionable items for triage',
      count: detailedItems.length,
      accentClass: 'text-blue-600 bg-blue-50 border-blue-200',
    },
  ];

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-7 gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900">User Feedback</h1>
          <p className="text-sm text-gray-500 mt-1">
            Review and manage feedback submitted by users across your Knowledge Assistants
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {isAdmin && (
            <select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              disabled={loadingClients}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white min-w-[200px]"
            >
              <option value="">All Clients</option>
              {clients.map((c) => (
                <option key={c._id} value={c._id}>{c.name || c.email}</option>
              ))}
            </select>
          )}
          <select
            value={selectedEventId}
            onChange={(e) => setSelectedEventId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white min-w-[210px]"
          >
            <option value="">Select Knowledge Assistant</option>
            {events.map((e) => (
              <option key={e._id} value={e._id}>{e.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tab cards */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative text-left p-5 rounded-xl border-2 transition-all ${
                isActive
                  ? 'border-blue-500 bg-white shadow-md shadow-blue-100'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              {isActive && (
                <span className="absolute top-4 right-4 w-2.5 h-2.5 bg-blue-500 rounded-full" />
              )}
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center border ${tab.accentClass}`}>
                  {tab.icon}
                </div>
                <div>
                  <p className={`text-sm font-semibold ${isActive ? 'text-blue-700' : 'text-gray-900'}`}>
                    {tab.label}
                  </p>
                  <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full mt-0.5 ${
                    isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {loading ? '…' : `${tab.count} item${tab.count !== 1 ? 's' : ''}`}
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">{tab.description}</p>
            </button>
          );
        })}
      </div>

      {/* Content panel */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <span className="ml-3 text-sm text-gray-500">Loading feedback…</span>
          </div>
        ) : !selectedEventId ? (
          <div className="flex flex-col items-center py-16">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <ClipboardList className="w-6 h-6 text-gray-300" />
            </div>
            <p className="text-gray-500 text-sm">Select a Knowledge Assistant to view feedback</p>
          </div>
        ) : activeTab === 'binary' ? (
          <BinaryTab
            key={selectedEventId + '-binary'}
            items={binaryItems}
            onViewMessage={handleViewMessage}
          />
        ) : (
          <DetailedTab
            key={selectedEventId + '-detailed'}
            items={detailedItems}
            teamMembers={teamMembers}
            onUpdateItem={handleUpdateItem}
            onViewMessage={handleViewMessage}
          />
        )}
      </div>

      {/* Conversation modal */}
      {viewingItem && (
        <ConversationModal
          item={viewingItem}
          conversations={conversations}
          loading={loadingConv}
          error={convError}
          onClose={() => { setViewingItem(null); setConversations([]); setConvError(null); }}
        />
      )}
    </div>
  );
};

export default Feedback;
